import React from "react";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import MobileAppBar from "../conversation/MobileAppBar";
import DesktopLogoConversationPanel from "../conversation/DesktopLogoConversationPanel";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";

export interface TilePageAnimationRef {
  start: () => void;
  stop: () => void;
}

interface TilePageProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  openDrawer: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  animationRef: React.RefObject<TilePageAnimationRef | null>;
  children: React.ReactNode;
  /** Optional element rendered inside the fixed panel but outside the scroll area (e.g. a FAB). */
  action?: React.ReactNode;
}

const TilePage: React.FC<TilePageProps> = ({
  title,
  subtitle,
  openDrawer,
  setOpenDrawer,
  animationRef,
  children,
  action,
}) => {
  const theme = useTheme();
  const isScreenBiggerThanOrEqualToMd = useMediaQuery(
    theme.breakpoints.up("md"),
  );
  const isScreenBiggerThanOrEqualToLg = useMediaQuery(
    theme.breakpoints.up("lg"),
  );

  return (
    <Box>
      {isScreenBiggerThanOrEqualToLg && (
        <DesktopLogoConversationPanel refAnimation={animationRef} />
      )}
      <Box
        sx={[
          {
            position: "fixed",
            top: "50%",
            left: "calc(50%)",
            transform: "translate(-50%, -50%)",
            height: "100%",
            width: {
              xs: "100vw",
              md: "min(92vw, 980px)",
              lg: "min(82vw, 1080px)",
              xl: "min(78vw, 1240px)",
            },
            backgroundColor: theme.palette.secondary.main,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: theme.transitions.create(["left", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
          isScreenBiggerThanOrEqualToMd &&
            openDrawer && {
              left: `calc(50% + ${SETTINGS.drawerWidth / 2}px)`,
              width: {
                md: `min(calc(92vw - ${SETTINGS.drawerWidth}px), 980px)`,
                lg: `min(calc(82vw - ${SETTINGS.drawerWidth}px), 1080px)`,
                xl: `min(calc(78vw - ${SETTINGS.drawerWidth}px), 1240px)`,
              },
              transition: theme.transitions.create(["left", "width"], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
        ]}
      >
        {!isScreenBiggerThanOrEqualToLg && (
          <MobileAppBar
            openDrawer={openDrawer}
            anonymousSession={false}
            isScreenBiggerThanOrEqualToLg={isScreenBiggerThanOrEqualToLg}
            handleDrawerOpen={() => setOpenDrawer(true)}
          />
        )}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
            pt: { xs: `${SETTINGS.topMarginMobile}px`, lg: 4 },
            pb: { xs: 3, sm: 4 },
            scrollbarColor: "var(--tally-brown) var(--tally-white)",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {children}
          </Grid>
        </Box>
        {action}
      </Box>
    </Box>
  );
};

export default TilePage;
